import React, { useContext } from "react";
import Tree from "../../../../Components/Tree/Tree";
import type { TreeNode } from "../../../../Components/Tree/types";
import {
  filterTypes,
  findingsQueryParams,
  resourceTypes,
} from "../../../FindingsUtils";
import { LS_SCOPE, UNSAVED_SCOPE } from "../../FilterUtils";
import useFindingsQueryParam from "../../../FindingsData/useFindingsQueryParam";
import {
  ScopesContext,
  type ScopeNode,
  type ScopeType,
  type SelectedScope,
} from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import { Loader, ModalContext } from "@SeemplicityDev/shared";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../../General/AppPreferencesProvider";
import { showToast } from "../../../../Forms/Dialogs/Toast/Toast";
import DeleteScopeModal from "../ScopeGuardrails/DeleteScopeModal";
import DeleteScopeGroupModal from "../ScopeGuardrails/DeleteScopeGroupModal";
import EditScopeDetailsModal from "../ScopeGuardrails/EditScopeDetailsModal";
import EditScopeGroupModal, {
  type EditedGroupMembers,
} from "../ScopeGuardrails/EditScopeGroupModal";
import MoveScopeModal from "../ScopeGuardrails/MoveScopeModal";
import { ScopeManagementContext } from "../ScopeManagement/ScopeManagementContext";
import { getIconByType } from "../../../../Components/Tree/TreeUtils";
import { useHistory } from "react-router-dom";
import useSelectScope from "../../../../AppView/ScopeManagement/useSelectScope";
import FilterPreviewPopover from "../../../../AppView/FilterManagement/FilterPreview/FilterPreviewPopover";

interface ScopesListProps {
  editable?: boolean;
  noScopes?: boolean;
  toggleIsOpen: () => void;
  selectableNodeType?: ScopeType;
  selectedNode?: string | null;
  onScopeSelect?: ((node: TreeNode) => void) | null;
  isSmall?: boolean;
  scrollToSelected?: boolean;
  displayAsInput?: boolean;
}

const ScopesList: React.FC<ScopesListProps> = ({
  editable: editableScopes,
  noScopes,
  toggleIsOpen,
  selectableNodeType,
  selectedNode,
  onScopeSelect = null,
  isSmall,
  scrollToSelected,
  displayAsInput,
}) => {
  const {
    scopesState: { scopesList, selectedScope, defaultScope },
    updateScopeState,
  } = useContext(ScopesContext);
  const { setDefaultsForScopeAndFilter } = useContext(ScopeManagementContext);
  const { selectScope, isScopeSelected } = useSelectScope();

  const { clearQueryParam, search } = useFindingsQueryParam(
    findingsQueryParams.SCOPE_ID
  );

  const { openModal } = useContext(ModalContext);
  const { push } = useHistory();

  const { isAllowed } = useContext(AppPreferencesContext);

  const isActive = (id: string | undefined) => selectedScope?.value?.id === id;

  const syncActiveScope = (updated: SelectedScope) => {
    updateScopeState({ selectedScope: updated });
    if (setDefaultsForScopeAndFilter) {
      localStorage.setItem(LS_SCOPE, JSON.stringify(updated));
    }
    if (!!search) {
      clearQueryParam([findingsQueryParams.SCOPE_ID]);
    }
  };

  const afterScopeRenamed = (id: string, name: string) => {
    if (isActive(id) && selectedScope) {
      syncActiveScope({ ...selectedScope, label: name });
    }
    showToast({
      message: (
        <div className={"modal-text"}>
          The Scope <span className={"modal-live-text"}>{name}</span> was
          updated successfully
        </div>
      ),
      "data-testid": "scope-updated-success-toast",
    });
  };

  const afterGroupEdited = (
    group: TreeNode,
    name: string,
    members: EditedGroupMembers
  ) => {
    const activeId = selectedScope?.value?.id;
    if (isActive(group.id) && selectedScope) {
      syncActiveScope({ ...selectedScope, label: name });
    } else if (selectedScope && activeId) {
      const isMember =
        members.childScopeIds.includes(activeId) ||
        members.childGroupIds.includes(activeId);
      const wasMember = selectedScope.parentId === group.id;
      if (wasMember && !isMember) {
        // Dropped members land at the top level.
        syncActiveScope({ ...selectedScope, parentId: null, level: 1 });
      } else if (!wasMember && isMember) {
        syncActiveScope({
          ...selectedScope,
          parentId: group.id,
          level:
            ((group as { level?: number }).level ?? NaN) + 1 ||
            selectedScope.level,
        });
      }
    }
    showToast({
      message: (
        <div className={"modal-text"}>
          The Group <span className={"modal-live-text"}>{name}</span> was
          updated successfully
        </div>
      ),
      "data-testid": "group-updated-success-toast",
    });
  };

  const afterMoved = (node: TreeNode, location: TreeNode) => {
    if (isActive(node.id) && selectedScope) {
      syncActiveScope({
        ...selectedScope,
        parentId: location.type === resourceTypes.ROOT ? null : location.id,
        level:
          ((location as { level?: number }).level ?? NaN) + 1 ||
          selectedScope.level,
      });
    }
    const noun = node.type === resourceTypes.SCOPE ? "Scope" : "Group";
    showToast({
      message: (
        <div className={"modal-text"}>
          The {noun} <span className={"modal-live-text"}>{node.name}</span> was
          moved successfully
        </div>
      ),
      "data-testid": `${node.type}-moved-success-toast`,
    });
  };

  const onEditScope = (node: TreeNode) => {
    const id = node?.id;
    if (!id) {
      return;
    }
    toggleIsOpen();
    if (node.type === resourceTypes.SCOPE) {
      openModal({
        title: "Edit Scope Details",
        size: "s",
        overlay: true,
        component: (
          <EditScopeDetailsModal
            scopeId={id}
            scopeName={node.name}
            onRenamed={(name) => afterScopeRenamed(id, name)}
          />
        ),
      });
    } else if (node.type === resourceTypes.GROUP) {
      openModal({
        title: "Edit Scope Group Details",
        size: "s",
        overlay: true,
        component: (
          <EditScopeGroupModal
            groupId={id}
            groupName={node.name}
            root={(scopesList?.[0] as ScopeNode) ?? null}
            onEdited={(name, members) => afterGroupEdited(node, name, members)}
          />
        ),
      });
    }
  };

  const onMoveScope = (node: TreeNode) => {
    const id = node?.id;
    if (
      !id ||
      (node.type !== resourceTypes.SCOPE && node.type !== resourceTypes.GROUP)
    ) {
      return;
    }
    toggleIsOpen();
    openModal({
      title:
        node.type === resourceTypes.SCOPE
          ? "Edit Scope Location"
          : "Edit Scope Group Location",
      size: "s",
      overlay: true,
      component: (
        <MoveScopeModal
          nodeId={id}
          nodeName={node.name}
          nodeType={node.type === resourceTypes.SCOPE ? "scope" : "group"}
          root={(scopesList?.[0] as ScopeNode) ?? null}
          currentParentId={node.parentId}
          onMoved={(location) => afterMoved(node, location)}
        />
      ),
    });
  };

  const onSelectScope = (node: TreeNode) => {
    toggleIsOpen();

    if (!!onScopeSelect) {
      onScopeSelect?.(node);
      return;
    }

    // `selectScope` guards on this too; checked here so the query-param clear
    // below stays skipped for an already-selected node, as it always has been.
    if (isScopeSelected(node.id)) {
      return;
    }

    if (!!search) {
      clearQueryParam([findingsQueryParams.SCOPE_ID]);
    }

    selectScope({
      id: node.id,
      name: node.name,
      type: node.type as ScopeType | undefined,
      editable: node.editable as boolean | undefined,
      children: node.children as unknown as SelectedScope[] | undefined,
      level: node.level as number | undefined,
      parentId: node.parentId,
      filterData: node.filter_data,
    });
  };

  const afterScopeDeleted = (
    id: string | undefined,
    name: string | undefined
  ) => {
    if (id === selectedScope?.value?.id) {
      updateScopeState({
        scopeToExecute: defaultScope?.value?.query,
        selectedScope: defaultScope,
      });
    }
    localStorage.removeItem(LS_SCOPE);

    showToast({
      message: (
        <div className={"modal-text"}>
          The Scope <span className={"modal-live-text"}>{name}</span> was
          deleted successfully
        </div>
      ),
      "data-testid": "scope-deleted-success-toast",
    });
  };

  const afterGroupDeleted = (name: string | undefined) => {
    updateScopeState({
      scopeToExecute: null,
      selectedScope: null,
    });
    localStorage.removeItem(LS_SCOPE);

    showToast({
      message: (
        <div className={"modal-text"}>
          The Group <span className={"modal-live-text"}>{name}</span> was
          deleted successfully
        </div>
      ),
      "data-testid": "scope-group-deleted-success-toast",
    });
  };

  const onDelete = (nodeData: TreeNode) => {
    const id = nodeData?.id;
    if (!id) {
      return;
    }
    // Close the scopes popup so the modal isn't rendered behind it, as Edit does.
    toggleIsOpen();
    if (nodeData?.type === resourceTypes.SCOPE) {
      openModal({
        title: "Delete Scope",
        size: "s",
        overlay: true,
        component: (
          <DeleteScopeModal
            scopeId={id}
            scopeName={nodeData.name}
            onDeleted={() => afterScopeDeleted(id, nodeData.name)}
          />
        ),
      });
    } else {
      openModal({
        title: "Delete Scope Group",
        size: "s",
        overlay: true,
        component: (
          <DeleteScopeGroupModal
            groupId={id}
            groupName={nodeData.name}
            root={(scopesList?.[0] as ScopeNode) ?? null}
            onDeleted={() => afterGroupDeleted(nodeData.name)}
          />
        ),
      });
    }
  };

  return (
    <>
      {noScopes ? (
        <Loader
          className={"empty-state-scopes-menu"}
          flexcenter
          largeTitle
          largeButtons
          alignTop
          icon={getIconByType(resourceTypes.SCOPE)}
          title={"Create Your First Scope"}
          verticalButtons={displayAsInput}
          subtitle={
            isAllowed(userActionsEnum.CREATE_SCOPE)
              ? `Scopes represents collections of Resources combined together based on selected parameters`
              : "Insufficient permissions for creating Scopes. Contact the Seemplicity administrator for more information."
          }
          action={
            isAllowed(userActionsEnum.CREATE_SCOPE)
              ? {
                  label: `Create New Scope`,
                  onClick: () => {
                    if (displayAsInput) {
                      push("/scopes-generator");
                    } else {
                      updateScopeState({
                        isNewScope: true,
                        selectedScope: {
                          label: UNSAVED_SCOPE,
                          level: 1, // from here always under root scope,
                          value: {
                            type: resourceTypes.SCOPE as ScopeType,
                          },
                        },
                        scopeToExecute: null,
                      });
                      toggleIsOpen();
                    }
                  },
                }
              : null
          }
          secondaryAction={
            displayAsInput
              ? {
                  label: "Use Primary Scope",
                  leftIcon: getIconByType(resourceTypes.ROOT),
                  onClick: () => {
                    onSelectScope(scopesList?.[0] as unknown as TreeNode);
                  },
                }
              : null
          }
        />
      ) : (
        <Tree
          data={scopesList as unknown as TreeNode[] | null}
          nodeOperations={{
            onEdit: onEditScope,
            onMove: onMoveScope,
            onSelect: onSelectScope,
            onDelete: onDelete,
          }}
          getActionTooltips={(node) => {
            const noun =
              node.type === resourceTypes.GROUP ? "Scope Group" : "Scope";
            return {
              edit: `Edit ${noun} Details`,
              move: `Edit ${noun} Location`,
            };
          }}
          isSearchable={!!scopesList?.[0]?.children?.length}
          isSelectableNode={
            selectableNodeType
              ? (node) => node?.type === selectableNodeType
              : (node) => {
                  return node?.type === resourceTypes.GROUP
                    ? !!node?.has_scope_descendants
                    : true;
                }
          }
          data-testid={"resources-tree"}
          editableItems={
            editableScopes &&
            isAllowed(userActionsEnum.EDIT_SCOPE) &&
            isAllowed(userActionsEnum.DELETE_SCOPE)
          }
          getNodeActions={(node) =>
            node?.type === resourceTypes.SCOPE &&
            !!node?.filter_data && (
              <FilterPreviewPopover
                filterQuery={JSON.stringify(node.filter_data)}
                filterType={filterTypes.SCOPE}
              />
            )
          }
          selectedNode={selectedNode || selectedScope?.value?.id}
          placeholder={"Search Scopes..."}
          isSmall={isSmall}
          scrollToSelected={scrollToSelected}
          tooltipContent={(nodeData) =>
            nodeData?.type === resourceTypes.GROUP &&
            !nodeData?.has_scope_descendants
              ? "This scope group is empty. Assign a scope to it, or delete it."
              : null
          }
        />
      )}
    </>
  );
};

export default ScopesList;
